/**
 * Double Ratchet (Signal-style) for Lynx DM sessions.
 */

import { b64ToBytes, bytesToB64, textEncoder } from './bytes';
import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  dh,
  encodeWireBody,
  generateX25519Pair,
  kdfCk,
  kdfRk,
  MAX_SKIP,
} from './cryptoCore';

const headerAad = (header) => textEncoder.encode(JSON.stringify({
  dh: header.dh,
  n: header.n,
  pn: header.pn,
  type: header.type || 'msg',
}));

export const createEmptyState = () => ({
  DHs: null,
  DHr: null,
  RK: null,
  CKs: null,
  CKr: null,
  Ns: 0,
  Nr: 0,
  PN: 0,
  MKSKIPPED: {},
  peerIdentityKey: null,
  peerDeviceId: null,
  established: false,
  pendingPrekey: null,
});

export const serializeState = (state) => ({
  DHs: state.DHs ? {
    secretKey: bytesToB64(state.DHs.secretKey),
    publicKey: bytesToB64(state.DHs.publicKey),
  } : null,
  DHr: state.DHr ? bytesToB64(state.DHr) : null,
  RK: state.RK ? bytesToB64(state.RK) : null,
  CKs: state.CKs ? bytesToB64(state.CKs) : null,
  CKr: state.CKr ? bytesToB64(state.CKr) : null,
  Ns: state.Ns || 0,
  Nr: state.Nr || 0,
  PN: state.PN || 0,
  MKSKIPPED: state.MKSKIPPED || {},
  peerIdentityKey: state.peerIdentityKey ? bytesToB64(state.peerIdentityKey) : null,
  peerDeviceId: state.peerDeviceId || null,
  established: Boolean(state.established),
  pendingPrekey: state.pendingPrekey || null,
});

export const deserializeState = (raw) => {
  if (!raw) return createEmptyState();
  return {
    DHs: raw.DHs ? {
      secretKey: b64ToBytes(raw.DHs.secretKey),
      publicKey: b64ToBytes(raw.DHs.publicKey),
    } : null,
    DHr: raw.DHr ? b64ToBytes(raw.DHr) : null,
    RK: raw.RK ? b64ToBytes(raw.RK) : null,
    CKs: raw.CKs ? b64ToBytes(raw.CKs) : null,
    CKr: raw.CKr ? b64ToBytes(raw.CKr) : null,
    Ns: raw.Ns || 0,
    Nr: raw.Nr || 0,
    PN: raw.PN || 0,
    MKSKIPPED: raw.MKSKIPPED || {},
    peerIdentityKey: raw.peerIdentityKey ? b64ToBytes(raw.peerIdentityKey) : null,
    peerDeviceId: raw.peerDeviceId || null,
    established: Boolean(raw.established),
    pendingPrekey: raw.pendingPrekey || null,
  };
};

/** Alice initiates after X3DH shared secret. */
export const initAlice = (sharedSecret, bobRatchetPublic, peerIdentityPublic, peerDeviceId, prekeyMeta) => {
  const state = createEmptyState();
  state.DHs = generateX25519Pair();
  state.peerIdentityKey = peerIdentityPublic;
  state.peerDeviceId = peerDeviceId;
  const { rootKey, chainKey } = kdfRk(sharedSecret, dh(state.DHs.secretKey, bobRatchetPublic));
  state.RK = rootKey;
  state.CKs = chainKey;
  state.DHr = bobRatchetPublic;
  state.established = true;
  state.pendingPrekey = {
    type: 'prekey',
    spk_id: prekeyMeta.spkId,
    opk_id: prekeyMeta.opkId,
    ek: bytesToB64(prekeyMeta.ephemeralPublic),
    ik: bytesToB64(prekeyMeta.identityPublic),
  };
  return state;
};

/** Bob responds after X3DH — receiving chain opens on first decrypt. */
export const initBob = (sharedSecret, bobRatchetKeyPair, peerIdentityPublic, peerDeviceId) => {
  const state = createEmptyState();
  state.DHs = bobRatchetKeyPair;
  state.RK = sharedSecret;
  state.peerIdentityKey = peerIdentityPublic;
  state.peerDeviceId = peerDeviceId;
  state.established = true;
  return state;
};

const skipMessageKeys = (state, until) => {
  if (!state.CKr) {
    throw new Error('no receiving chain');
  }
  if (until - state.Nr > MAX_SKIP) {
    throw new Error('too many skipped messages');
  }
  while (state.Nr < until) {
    const { messageKey, chainKey } = kdfCk(state.CKr);
    state.CKr = chainKey;
    const dhKey = bytesToB64(state.DHr);
    state.MKSKIPPED[`${dhKey}:${state.Nr}`] = bytesToB64(messageKey);
    state.Nr += 1;
  }
};

const dhRatchet = (state, remotePublic) => {
  state.PN = state.Ns;
  state.Ns = 0;
  state.Nr = 0;
  state.DHr = remotePublic;
  let step = kdfRk(state.RK, dh(state.DHs.secretKey, state.DHr));
  state.RK = step.rootKey;
  state.CKr = step.chainKey;
  state.DHs = generateX25519Pair();
  step = kdfRk(state.RK, dh(state.DHs.secretKey, state.DHr));
  state.RK = step.rootKey;
  state.CKs = step.chainKey;
};

export const ratchetEncrypt = async (state, plaintext) => {
  if (!state.CKs || !state.DHs) {
    throw new Error('session not ready to send');
  }
  const { messageKey, chainKey } = kdfCk(state.CKs);
  state.CKs = chainKey;
  const header = {
    v: 1,
    type: state.pendingPrekey ? 'prekey' : 'msg',
    dh: bytesToB64(state.DHs.publicKey),
    n: state.Ns,
    pn: state.PN,
  };
  if (state.pendingPrekey) {
    Object.assign(header, state.pendingPrekey);
    state.pendingPrekey = null;
  }
  state.Ns += 1;
  const plain = typeof plaintext === 'string' ? textEncoder.encode(plaintext) : plaintext;
  const packed = await aesGcmEncrypt(messageKey, plain, headerAad(header));
  return {
    header,
    body: encodeWireBody(packed),
  };
};

export const ratchetDecrypt = async (state, header, packedCipher) => {
  const snapshot = serializeState(state);
  try {
    const dhB64 = header.dh;
    const skippedKey = `${dhB64}:${header.n}`;
    if (state.MKSKIPPED[skippedKey]) {
      const mk = b64ToBytes(state.MKSKIPPED[skippedKey]);
      delete state.MKSKIPPED[skippedKey];
      return await aesGcmDecrypt(mk, packedCipher, headerAad(header));
    }

    const remotePublic = b64ToBytes(dhB64);
    if (!state.DHr || bytesToB64(state.DHr) !== dhB64) {
      if (state.CKr) {
        skipMessageKeys(state, header.pn);
      }
      dhRatchet(state, remotePublic);
    }
    skipMessageKeys(state, header.n);
    const { messageKey, chainKey } = kdfCk(state.CKr);
    state.CKr = chainKey;
    state.Nr += 1;
    return await aesGcmDecrypt(messageKey, packedCipher, headerAad(header));
  } catch (err) {
    // dhRatchet runs before AEAD — roll back so a failed decrypt cannot poison the session.
    const restored = deserializeState(snapshot);
    Object.keys(state).forEach((k) => { delete state[k]; });
    Object.assign(state, restored);
    throw err;
  }
};

/**
 * Advance the sending chain to match an already-sent header (history replay).
 * Own ciphertext cannot be ratchet-decrypted; we only sync Ns/CKs when DH matches.
 */
export const replayOwnSend = (state, header) => {
  if (!state?.established || !state.CKs || !state.DHs || !header?.dh) {
    return false;
  }
  const localDh = bytesToB64(state.DHs.publicKey);
  if (localDh !== header.dh) {
    return false;
  }
  const target = Number(header.n);
  if (!Number.isFinite(target) || target < state.Ns) {
    return true;
  }
  while (state.Ns <= target) {
    const { chainKey } = kdfCk(state.CKs);
    state.CKs = chainKey;
    state.Ns += 1;
  }
  if (state.pendingPrekey) {
    state.pendingPrekey = null;
  }
  return true;
};
